import type { RichTranslationValues } from 'next-intl';
import { useTranslations as useNextIntlTranslations } from 'next-intl';
import { useCallback, useMemo } from 'react';

export type TFunction = ReturnType<typeof useTranslations>;

export const useTranslations = (
  namespace?: Parameters<typeof useNextIntlTranslations>[0],
) => {
  const nextIntlT = useNextIntlTranslations(namespace);

  const tBase = useCallback(
    (key: string, values?: Record<string, string>) => {
      try {
        // Just assume key is supported
        return nextIntlT(key as any, values);
      } catch {
        console.warn(`Translation key "${key}" not found`);
        return key;
      }
    },
    [nextIntlT],
  );

  const tRich = useCallback(
    (key: string, values?: RichTranslationValues) => {
      try {
        // Just assume key is supported
        return nextIntlT.rich(key as any, values);
      } catch {
        console.warn(`Translation key "${key}" not found`);
        return key;
      }
    },
    [nextIntlT],
  );

  // Translations come from the CMS, so a key can be absent in a given locale.
  const tHas = useCallback(
    (key: string) => nextIntlT.has(key as any),
    [nextIntlT],
  );

  const t = useMemo(
    () =>
      Object.assign(tBase, {
        rich: tRich,
        has: tHas,
      }),
    [tBase, tRich, tHas],
  );

  return t;
};

export default useTranslations;
